import { AxiosResponse } from 'axios'

import {
  DEV_PACKAGE_GUID,
  featureFromGuid,
  useFeatureAccessEmulatorStore,
} from '@/shared/composables/user-features'
import {
  Account,
  PackageOfServices,
  PermissionGroup,
  PermissionGroupUpdateItem,
  ServicesList,
} from '@/shared/types/settings'
import { getAppURL } from '@/shared/utils/electron'
import { FetchResponse, useFetchApi } from '@/shared/utils/fetch'

export const useAPISettingsPermissionAndServices = () => {
  const useFetch = useFetchApi()

  const getEmulator = () => {
    try {
      return useFeatureAccessEmulatorStore()
    } catch {
      return null
    }
  }

  const getPermissionGroup = async (): Promise<AxiosResponse<PermissionGroup[]> | FetchResponse<PermissionGroup[]>> => {
    const result = await useFetch.get<PermissionGroup[]>(getAppURL({ pathName: '/api/administrative-group' }))
    const emulator = getEmulator()
    if (emulator?.isIntercepting) return emulator.applyGroupsOverlay(result)
    return result
  }

  const getServicesList = async (): Promise<AxiosResponse<ServicesList[]> | FetchResponse<ServicesList[]>> => {
    const result = await useFetch.get<ServicesList[]>(getAppURL({ pathName: '/api/feature' }))
    const emulator = getEmulator()
    if (emulator?.isIntercepting) return emulator.applyServicesOverlay(result)
    return result
  }

  const getPackageOfServicesLists = async (): Promise<AxiosResponse<PackageOfServices[]> | FetchResponse<PackageOfServices[]>> => {
    const result = await useFetch.get<PackageOfServices[]>(getAppURL({ pathName: '/api/feature-package' }))
    const emulator = getEmulator()
    if (emulator?.isIntercepting) return emulator.applyPackagesOverlay(result)
    return result
  }

  const updatePermissionGroup = (updateItem: PermissionGroupUpdateItem): Promise<AxiosResponse | FetchResponse> => {
    const emulator = getEmulator()
    if (emulator?.isIntercepting && featureFromGuid(updateItem.guid)) {
      return Promise.resolve(emulator.mockUpdatePermissionGroup(updateItem))
    }
    return useFetch.put(getAppURL({ pathName: '/api/administrative-group' }), updateItem)
  }

  const updateUserServices = (updateItem: Account): Promise<AxiosResponse | FetchResponse> => {
    return useFetch.put(getAppURL({ pathName: '/api/User/who-am-i' }), updateItem)
  }

  const updatePackageOfServices = (updateItem: {
    guid: string;
    enabled: boolean;
  }): Promise<AxiosResponse | FetchResponse> => {
    const emulator = getEmulator()
    if (emulator?.isIntercepting && updateItem.guid === DEV_PACKAGE_GUID) {
      return Promise.resolve(emulator.mockUpdatePackageOfServices())
    }
    return useFetch.put(getAppURL({ pathName: '/api/feature-package' }), updateItem)
  }

  return {
    getPermissionGroup,
    getServicesList,
    getPackageOfServicesLists,
    updatePermissionGroup,
    updateUserServices,
    updatePackageOfServices,
  }
}
